import { Button, Result } from 'antd';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureError } from '@/services/telemetry';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Console error is allowed for critical error logging
    console.error('Error Boundary caught:', error, errorInfo);
    captureError(error, { componentStack: errorInfo.componentStack ?? null });
    this.setState({ errorInfo });
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '50px' }}>
          <Result
            status="error"
            title="Something went wrong"
            subTitle={
              this.state.error?.message ?? 'An unexpected error occurred. Please try again later.'
            }
            extra={[
              <Button type="primary" key="home" onClick={this.handleReset}>
                Go to Dashboard
              </Button>,
              <Button
                key="reload"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Reload Page
              </Button>,
            ]}
          />
          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <details style={{ marginTop: '24px', whiteSpace: 'pre-wrap' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Error Details (Development Only)
              </summary>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--app-bg)',
                  borderRadius: '4px',
                }}
              >
                <strong>Component Stack:</strong>
                <pre>{this.state.errorInfo.componentStack}</pre>
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
